/**
 * Responsive layout computation — pure function, no side effects.
 * Three breakpoints: portrait (mobile), landscape (tablet), desktop.
 */

export type LayoutMode = "portrait" | "landscape" | "desktop";

export interface ResponsiveLayout {
  readonly layoutMode: LayoutMode;
  readonly canvasSize: number;
  readonly controllerSize: number;
  readonly showSidePanel: boolean;
  readonly padding: number;
}

const MOBILE_MAX_WIDTH = 480;
const DESKTOP_MIN_WIDTH = 1025;

const MIN_CANVAS = 200;
const MAX_CANVAS = 600;

const TITLE_BAR = 44;
const ACTION_BAR = 56;
const GAPS = 24;
const CHROME_HEIGHT = TITLE_BAR + ACTION_BAR + GAPS;

const SIDEBAR_WIDTH = 240;

function clampCanvas(size: number): number {
  return Math.max(MIN_CANVAS, Math.min(MAX_CANVAS, Math.round(size)));
}

function computeLayoutMode(
  windowWidth: number,
  windowHeight: number,
): LayoutMode {
  if (windowWidth <= MOBILE_MAX_WIDTH) return "portrait";
  if (windowWidth >= DESKTOP_MIN_WIDTH) return "desktop";
  return windowWidth > windowHeight ? "landscape" : "portrait";
}

export function computeResponsiveLayout(
  windowWidth: number,
  windowHeight: number,
): ResponsiveLayout {
  const layoutMode = computeLayoutMode(windowWidth, windowHeight);

  let canvasSize: number;
  let controllerSize: number;
  let padding: number;

  switch (layoutMode) {
    case "portrait": {
      padding = 8;
      const availableWidth = windowWidth - padding * 2;
      const availableHeight = (windowHeight - CHROME_HEIGHT) * 0.6;
      canvasSize = clampCanvas(Math.min(availableWidth, availableHeight));
      controllerSize = Math.round(canvasSize * 0.7);
      break;
    }
    case "landscape": {
      padding = 12;
      const availableHeight = windowHeight - CHROME_HEIGHT;
      const availableWidth = windowWidth * 0.6;
      canvasSize = clampCanvas(Math.min(availableHeight, availableWidth));
      controllerSize = Math.round(canvasSize * 0.5);
      break;
    }
    case "desktop": {
      padding = 16;
      const availableHeight = windowHeight - CHROME_HEIGHT;
      const availableWidth = (windowWidth - SIDEBAR_WIDTH) * 0.55;
      canvasSize = clampCanvas(Math.min(availableHeight, availableWidth));
      controllerSize = Math.round(canvasSize * 0.5);
      break;
    }
  }

  return {
    layoutMode,
    canvasSize,
    controllerSize,
    showSidePanel: layoutMode === "desktop",
    padding,
  };
}
